import React, { useState, useEffect, useRef } from 'react';
import { LegacyClaimant, LegacyClaimantAudit, createClaimantAudit, getLegacyClaimant, uploadLegacyClaimantDocument } from '../api/legacy';
import LegacyClaimCorrespondence from './LegacyClaimCorrespondence';
import EmailComposer from './EmailComposer';
import './LegacyClaimantDetails.css';


interface LegacyClaimantDetailsProps {
  claimant: LegacyClaimant;
  onClose: () => void;
  onUpdate?: (updatedClaimant: LegacyClaimant) => void;
}

interface AuditFormData {
  // DBA Section
  dba_signed_and_on_file?: boolean;
  dba_printed_name_signature_match_file?: boolean;
  dba_address_match_file?: boolean;
  dba_terms_of_business_agree_to_dba?: boolean;
  
  // FOA Section
  foa_signed_and_on_file?: boolean;
  foa_and_dba_signature_match?: boolean;
  
  // KYC Section
  kyc_credit_check_on_file?: boolean;
  kyc_name_match?: boolean;
  kyc_address_match?: boolean;
  kyc_pep_sanctions_clear?: boolean;
  kyc_electoral_roll_confirmed?: boolean;
  kyc_active_credit_accounts_found?: boolean;
  
  // Notes
  notes: string;
}

const LegacyClaimantDetails: React.FC<LegacyClaimantDetailsProps> = ({ 
  claimant, 
  onClose,
  onUpdate 
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'claims' | 'documents' | 'audit' | 'correspondence'>('details');
  const [showAuditForm, setShowAuditForm] = useState(false);
  const [auditFormData, setAuditFormData] = useState<AuditFormData>({
    notes: ''
  });
  const [isSubmittingAudit, setIsSubmittingAudit] = useState(false);
  const [auditMessage, setAuditMessage] = useState('');
  const [selectedAudit, setSelectedAudit] = useState<LegacyClaimantAudit | null>(null);
  const [currentClaimant, setCurrentClaimant] = useState<LegacyClaimant>(claimant);
  const [selectedClaimForCorrespondence, setSelectedClaimForCorrespondence] = useState<string | null>(null);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('correspondence');
  const [documentDescription, setDocumentDescription] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch complete claimant data on mount
  useEffect(() => {
    console.log('Initial claimant data:', claimant);
    console.log('Document URLs in initial data:', {
      clientCareLetterUrl: claimant.clientCareLetterUrl,
      identificationUrl: claimant.identificationUrl,
      formOfAuthorityUrl: claimant.formOfAuthorityUrl,
      letterOfAuthorityUrl: claimant.letterOfAuthorityUrl,
      letterOfComplaintUrl: claimant.letterOfComplaintUrl
    });
    
    // Fetch complete claimant data including claims
    refreshClaimantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Refresh claimant data when audit tab is opened
  useEffect(() => {
    if (activeTab === 'audit' && claimant.id) {
      refreshClaimantData();
    }
    
    // Auto-select first claim when correspondence tab is opened
    if (activeTab === 'correspondence' && currentClaimant.claims && currentClaimant.claims.length > 0 && !selectedClaimForCorrespondence) {
      setSelectedClaimForCorrespondence(currentClaimant.claims[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const refreshClaimantData = async () => {
    try {
      const updatedClaimant = await getLegacyClaimant(claimant.id);
      console.log('Refreshed claimant data:', updatedClaimant);
      console.log('Document URLs in refreshed data:', {
        clientCareLetterUrl: updatedClaimant.clientCareLetterUrl,
        identificationUrl: updatedClaimant.identificationUrl,
        formOfAuthorityUrl: updatedClaimant.formOfAuthorityUrl,
        letterOfAuthorityUrl: updatedClaimant.letterOfAuthorityUrl,
        letterOfComplaintUrl: updatedClaimant.letterOfComplaintUrl
      });
      setCurrentClaimant(updatedClaimant);
    } catch (error) {
      console.error('Failed to refresh claimant data:', error);
    }
  };

  const handleAuditInputChange = (field: keyof AuditFormData, value: boolean | string) => {
    setAuditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAuditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingAudit(true);
    setAuditMessage('');

    try {
      // Call the actual API
      await createClaimantAudit(claimant.id, auditFormData);
      
      setAuditMessage('Audit created successfully!');
      setShowAuditForm(false);
      
      // Reset form
      setAuditFormData({ notes: '' });
      
      // Refresh claimant data to get the new audit
      await refreshClaimantData();
      
      // Trigger refresh if needed
      if (onUpdate) {
        const updatedClaimant = await getLegacyClaimant(claimant.id);
        onUpdate(updatedClaimant);
      }
    } catch (error) {
      setAuditMessage('Failed to create audit. Please try again.');
      console.error('Error creating audit:', error);
    } finally {
      setIsSubmittingAudit(false);
    }
  };

  const renderYesNoField = (
    label: string,
    field: keyof AuditFormData,
    value: boolean | undefined
  ) => (
    <div className="lcd-audit-field">
      <label className="lcd-audit-label">{label}</label>
      <div className="lcd-radio-group">
        <label className="lcd-radio-option">
          <input
            type="radio"
            name={field}
            checked={value === true}
            onChange={() => handleAuditInputChange(field, true)}
          />
          Yes
        </label>
        <label className="lcd-radio-option">
          <input
            type="radio"
            name={field}
            checked={value === false}
            onChange={() => handleAuditInputChange(field, false)}
          />
          No
        </label>
      </div>
    </div>
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const renderAuditDetail = (label: string, value?: boolean) => {
    if (value === undefined) return null;
    return (
      <div className="lcd-audit-detail">
        <span className="lcd-audit-detail-label">{label}:</span>
        <span className={`lcd-audit-detail-value ${value ? 'yes' : 'no'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      </div>
    );
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null);
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    setUploadingDocument(true);
    setUploadError(null);

    try {
      const result = await uploadLegacyClaimantDocument(
        claimant.id,
        selectedFile,
        documentType,
        documentDescription,
        selectedClaimForCorrespondence || undefined
      );

      if (result.success) {
        // Close dialog and reset state
        setShowUploadDialog(false);
        setSelectedFile(null);
        setDocumentType('correspondence');
        setDocumentDescription('');
        
        // Update correspondence based on where it was saved
        if (result.savedTo === 'claimant') {
          // Update the claimant's correspondence
          if (!currentClaimant.correspondence) {
            currentClaimant.correspondence = [];
          }
          currentClaimant.correspondence.push(result.correspondence);
          setCurrentClaimant({...currentClaimant});
        } else {
          // Correspondence was saved to claim - the LegacyClaimCorrespondence component will refresh
          // We could force a refresh here if needed
        }
        
        // Show success message
        alert(`Document uploaded successfully to ${result.savedTo}!`);
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      setUploadError(error.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const renderAuditSection = (audit: LegacyClaimantAudit) => (
    <div className="lcd-audit-details-view">
      <div className="lcd-audit-section">
        <h5>DBA (Damage Based Agreement)</h5>
        {renderAuditDetail('DBA signed and on file', audit.dba_signed_and_on_file)}
        {renderAuditDetail('DBA printed name/signature match file', audit.dba_printed_name_signature_match_file)}
        {renderAuditDetail('DBA address match file', audit.dba_address_match_file)}
        {renderAuditDetail('Terms of business attached to DBA', audit.dba_terms_of_business_agree_to_dba)}
      </div>

      <div className="lcd-audit-section">
        <h5>FOA (Form of Authority)</h5>
        {renderAuditDetail('FOA signed and on file', audit.foa_signed_and_on_file)}
        {renderAuditDetail('FOA and DBA signature match', audit.foa_and_dba_signature_match)}
      </div>

      <div className="lcd-audit-section">
        <h5>KYC (Know Your Customer)</h5>
        {renderAuditDetail('Credit check on file', audit.kyc_credit_check_on_file)}
        {renderAuditDetail('Name match', audit.kyc_name_match)}
        {renderAuditDetail('Address match', audit.kyc_address_match)}
        {renderAuditDetail('PEP/Sanctions clear', audit.kyc_pep_sanctions_clear)}
        {renderAuditDetail('Electoral roll confirmed', audit.kyc_electoral_roll_confirmed)}
        {renderAuditDetail('Active credit accounts found', audit.kyc_active_credit_accounts_found)}
      </div>

      {audit.notes && (
        <div className="lcd-audit-section">
          <h5>Additional Notes</h5>
          <p className="lcd-audit-notes">{audit.notes}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="lcd-overlay" onClick={onClose}>
      <div className="lcd-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="lcd-header">
          <h2>Legacy Claimant Details</h2>
          <button className="lcd-close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Claimant Basic Info */}
        <div className="lcd-claimant-summary">
          <h3>{claimant.firstName} {claimant.lastName}</h3>
          <div className="lcd-summary-grid">
            <div className="lcd-summary-item">
              <span className="lcd-label">ID:</span>
              <span className="lcd-value">{claimant.id}</span>
            </div>
            <div className="lcd-summary-item">
              <span className="lcd-label">Email:</span>
              <span className="lcd-value">{claimant.email || 'N/A'}</span>
            </div>
            <div className="lcd-summary-item">
              <span className="lcd-label">Phone:</span>
              <span className="lcd-value">{claimant.phoneNumber || 'N/A'}</span>
            </div>
            <div className="lcd-summary-item">
              <span className="lcd-label">Audit Status:</span>
              <span className={`lcd-value ${currentClaimant.audited ? 'lcd-status-active' : 'lcd-status-pending'}`}>
                {currentClaimant.audited ? 'Audited' : 'Pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="lcd-tabs">
          <button
            className={`lcd-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            className={`lcd-tab ${activeTab === 'claims' ? 'active' : ''}`}
            onClick={() => setActiveTab('claims')}
          >
            Claims ({currentClaimant.claims?.length || 0})
          </button>
          <button
            className={`lcd-tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
          <button
            className={`lcd-tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Audit
          </button>
          <button
            className={`lcd-tab ${activeTab === 'correspondence' ? 'active' : ''}`}
            onClick={() => setActiveTab('correspondence')}
          >
            Correspondence
          </button>
        </div>

        {/* Tab Content */}
        <div className="lcd-content">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="lcd-details-section">
              <h4>Personal Information</h4>
              <dl className="lcd-details-list">
                <dt>Full Name</dt>
                <dd>{claimant.firstName} {claimant.lastName}</dd>
                
                <dt>Date of Birth</dt>
                <dd>{formatDate(claimant.dateOfBirth)}</dd>
                
                <dt>Email</dt>
                <dd>{claimant.email || 'Not provided'}</dd>
                
                <dt>Phone</dt>
                <dd>{claimant.phoneNumber || 'Not provided'}</dd>
              </dl>

              <h4>Address</h4>
              <dl className="lcd-details-list">
                <dt>Address Line 1</dt>
                <dd>{claimant.addressLine1 || 'Not provided'}</dd>
                
                {claimant.addressLine2 && (
                  <>
                    <dt>Address Line 2</dt>
                    <dd>{claimant.addressLine2}</dd>
                  </>
                )}
                
                <dt>Town/City</dt>
                <dd>{claimant.town || 'Not provided'}</dd>
                
                <dt>County</dt>
                <dd>{claimant.county || 'Not provided'}</dd>
                
                <dt>Postcode</dt>
                <dd>{claimant.postcode || 'Not provided'}</dd>
              </dl>

              <h4>Import Information</h4>
              <dl className="lcd-details-list">
                <dt>Legacy ID</dt>
                <dd>{claimant.legacyId}</dd>
                
                <dt>Imported Date</dt>
                <dd>{formatDate(claimant.importedAt)}</dd>
                
                <dt>Import Job ID</dt>
                <dd>{claimant.importJobId}</dd>
              </dl>
            </div>
          )}

          {/* Claims Tab */}
          {activeTab === 'claims' && (
            <div className="lcd-claims-section">
              {currentClaimant.claims && currentClaimant.claims.length > 0 ? (
                <table className="lcd-claims-table">
                  <thead>
                    <tr>
                      <th>Legacy ID</th>
                      <th>Lender</th>
                      <th>Agreement #</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Balance</th>
                      <th>Imported</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentClaimant.claims.map(claim => (
                      <tr key={claim.id}>
                        <td>{claim.legacyId}</td>
                        <td>{claim.lender}</td>
                        <td>{claim.agreementNumber}</td>
                        <td>
                          <span className={`lcd-claim-status lcd-status-${claim.status.toLowerCase()}`}>
                            {claim.status}
                          </span>
                        </td>
                        <td>{formatCurrency(claim.creditAmount)}</td>
                        <td>{formatCurrency(claim.balance)}</td>
                        <td>{formatDate(claim.importedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="lcd-empty-state">
                  <p>No claims found for this claimant.</p>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="lcd-documents-section">
              <h4>Imported Documents</h4>
              <div className="lcd-documents-list">
                {/* Client Care Letter */}
                <div className="lcd-document-item">
                  <div className="lcd-document-info">
                    <h5>Client Care Letter</h5>
                    <p className="lcd-document-description">Letter outlining the terms of service and client agreement</p>
                  </div>
                  <div className="lcd-document-action">
                    {currentClaimant.clientCareLetterUrl ? (
                      <a 
                        href={currentClaimant.clientCareLetterUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="lcd-download-button"
                        download
                      >
                        Download
                      </a>
                    ) : (
                      <span className="lcd-document-unavailable">Not available</span>
                    )}
                  </div>
                </div>

                {/* Identification Document */}
                <div className="lcd-document-item">
                  <div className="lcd-document-info">
                    <h5>Identification Document</h5>
                    <p className="lcd-document-description">Proof of identity documentation for the claimant</p>
                  </div>
                  <div className="lcd-document-action">
                    {currentClaimant.identificationUrl ? (
                      <a 
                        href={currentClaimant.identificationUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="lcd-download-button"
                        download
                      >
                        Download
                      </a>
                    ) : (
                      <span className="lcd-document-unavailable">Not available</span>
                    )}
                  </div>
                </div>

                {/* Form of Authority */}
                <div className="lcd-document-item">
                  <div className="lcd-document-info">
                    <h5>Form of Authority</h5>
                    <p className="lcd-document-description">Authorization form allowing representation on behalf of the claimant</p>
                  </div>
                  <div className="lcd-document-action">
                    {currentClaimant.formOfAuthorityUrl ? (
                      <a 
                        href={currentClaimant.formOfAuthorityUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="lcd-download-button"
                        download
                      >
                        Download
                      </a>
                    ) : (
                      <span className="lcd-document-unavailable">Not available</span>
                    )}
                  </div>
                </div>

                {/* Letter of Authority */}
                <div className="lcd-document-item">
                  <div className="lcd-document-info">
                    <h5>Letter of Authority</h5>
                    <p className="lcd-document-description">Letter granting legal authority to act on behalf of the claimant</p>
                  </div>
                  <div className="lcd-document-action">
                    {currentClaimant.letterOfAuthorityUrl ? (
                      <a 
                        href={currentClaimant.letterOfAuthorityUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="lcd-download-button"
                        download
                      >
                        Download
                      </a>
                    ) : (
                      <span className="lcd-document-unavailable">Not available</span>
                    )}
                  </div>
                </div>

                {/* Letter of Complaint */}
                <div className="lcd-document-item">
                  <div className="lcd-document-info">
                    <h5>Letter of Complaint</h5>
                    <p className="lcd-document-description">Formal complaint letter submitted regarding the claim</p>
                  </div>
                  <div className="lcd-document-action">
                    {currentClaimant.letterOfComplaintUrl ? (
                      <a 
                        href={currentClaimant.letterOfComplaintUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="lcd-download-button"
                        download
                      >
                        Download
                      </a>
                    ) : (
                      <span className="lcd-document-unavailable">Not available</span>
                    )}
                  </div>
                </div>
              </div>

              {!currentClaimant.clientCareLetterUrl && !currentClaimant.identificationUrl && !currentClaimant.formOfAuthorityUrl && !currentClaimant.letterOfAuthorityUrl && !currentClaimant.letterOfComplaintUrl && (
                <div className="lcd-empty-state">
                  <p>No documents have been imported for this claimant.</p>
                </div>
              )}
            </div>
          )}


          {/* Audit Tab */}
          {activeTab === 'audit' && (
            <div className="lcd-audit-section">
              <div className="lcd-audit-header">
                <h4>Audit History</h4>
                <button 
                  className="lcd-create-audit-button"
                  onClick={() => setShowAuditForm(true)}
                >
                  Create New Audit
                </button>
              </div>

              {currentClaimant.audits && currentClaimant.audits.length > 0 ? (
                <div className="lcd-audit-list">
                  {currentClaimant.audits.map((audit, index) => (
                    <div key={audit.id || index} className="lcd-audit-item">
                      <div className="lcd-audit-item-header">
                        <span className="lcd-audit-date">
                          {formatDate(audit.auditedAt)}
                        </span>
                        <span className="lcd-audit-status lcd-status-completed">
                          Completed
                        </span>
                        <button
                          className="lcd-view-audit-button"
                          onClick={() => setSelectedAudit(selectedAudit?.id === audit.id ? null : audit)}
                        >
                          {selectedAudit?.id === audit.id ? 'Hide Details' : 'View Details'}
                        </button>
                      </div>
                      <div className="lcd-audit-item-body">
                        <p><strong>Audited by:</strong> {audit.auditedBy || 'Unknown'}</p>
                      </div>
                      
                      {/* Show audit details when selected */}
                      {selectedAudit?.id === audit.id && (
                        <div className="lcd-audit-item-details">
                          {renderAuditSection(audit)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="lcd-empty-state">
                  <p>No audit history found for this claimant.</p>
                  <button 
                    className="lcd-create-audit-button"
                    onClick={() => setShowAuditForm(true)}
                  >
                    Create First Audit
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Correspondence Tab */}
          {activeTab === 'correspondence' && (
            <div className="lcd-correspondence-section">
              {currentClaimant.claims && currentClaimant.claims.length > 0 ? (
                <>
                  {/* Claim Selection */}
                  <div className="lcd-claim-selector">
                    <h4>Select a claim to view correspondence:</h4>
                    <div className="lcd-claim-selector-grid">
                      {currentClaimant.claims.map(claim => (
                        <div 
                          key={claim.id}
                          className={`lcd-claim-selector-item ${selectedClaimForCorrespondence === claim.id ? 'selected' : ''}`}
                          onClick={() => setSelectedClaimForCorrespondence(claim.id)}
                        >
                          <div className="lcd-claim-selector-header">
                            <span className="lcd-claim-selector-id">{claim.legacyId}</span>
                            <span className={`lcd-claim-status lcd-status-${claim.status.toLowerCase()}`}>
                              {claim.status}
                            </span>
                          </div>
                          <div className="lcd-claim-selector-details">
                            <p><strong>Lender:</strong> {claim.lender}</p>
                            <p><strong>Agreement:</strong> {claim.agreementNumber}</p>
                            <p><strong>Amount:</strong> {formatCurrency(claim.creditAmount)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Correspondence Display */}
                  {selectedClaimForCorrespondence ? (
                    <div className="lcd-correspondence-viewer">
                      <h4>Correspondence for Claim {currentClaimant.claims.find(c => c.id === selectedClaimForCorrespondence)?.legacyId}</h4>
                      <div className="lcd-correspondence-actions">
                        <button 
                          className="lcd-add-correspondence-button"
                          onClick={() => setShowEmailComposer(true)}
                        >
                          + Send Email
                        </button>
                        <button 
                          className="lcd-upload-correspondence-button"
                          onClick={() => setShowUploadDialog(true)}
                        >
                          Upload Document
                        </button>
                      </div>
                      <LegacyClaimCorrespondence
                        claimId={selectedClaimForCorrespondence}
                      />
                    </div>
                  ) : (
                    <div className="lcd-empty-state">
                      <p>Please select a claim above to view its correspondence.</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="lcd-correspondence-section">
                  <div className="lcd-correspondence-header">
                    <h3>Claimant Correspondence</h3>
                    <p className="lcd-correspondence-note">
                      This claimant has no claims yet. You can still manage correspondence for future reference.
                    </p>
                  </div>
                  <div className="lcd-correspondence-actions">
                    <button 
                      className="lcd-add-correspondence-button"
                      onClick={() => setShowEmailComposer(true)}
                    >
                      + Send Email
                    </button>
                    <button 
                      className="lcd-upload-correspondence-button"
                      onClick={() => setShowUploadDialog(true)}
                      disabled={false}
                    >
                      Upload Document
                    </button>
                  </div>
                  {/* Show claimant-level correspondence */}
                  {currentClaimant.correspondence && currentClaimant.correspondence.length > 0 ? (
                    <div className="lcd-correspondence-list">
                      <h4>Claimant Correspondence</h4>
                      {currentClaimant.correspondence.map((item) => (
                        <div key={item.id} className="lcd-correspondence-item">
                          <div className="lcd-correspondence-icon">
                            {item.type === 'document' ? '📎' : '📄'}
                          </div>
                          <div className="lcd-correspondence-details">
                            <div className="lcd-correspondence-subject">
                              {item.subject || 'Document'}
                            </div>
                            <div className="lcd-correspondence-meta">
                              <span>{item.documentType || item.type}</span>
                              <span>•</span>
                              <span>{formatDate(item.date)}</span>
                              {item.attachmentUrl && (
                                <span>•</span>
                              )}
                              {item.attachmentUrl && (
                                <a href={item.attachmentUrl} target="_blank" rel="noopener noreferrer">
                                  Download
                                </a>
                              )}
                            </div>
                            {item.content && (
                              <div className="lcd-correspondence-preview">
                                {item.content}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="lcd-empty-state">
                      <p>No correspondence found for this claimant.</p>
                      <p className="lcd-empty-state-hint">Use the buttons above to add correspondence.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Audit Form Modal */}
        {showAuditForm && (
          <div className="lcd-audit-form-overlay" onClick={() => setShowAuditForm(false)}>
            <div className="lcd-audit-form-modal" onClick={(e) => e.stopPropagation()}>
              <div className="lcd-audit-form-header">
                <h3>Create Audit - {claimant.firstName} {claimant.lastName}</h3>
                <button 
                  className="lcd-close-button" 
                  onClick={() => setShowAuditForm(false)}
                >
                  &times;
                </button>
              </div>

              <form onSubmit={handleAuditSubmit} className="lcd-audit-form">
                {/* DBA Section */}
                <div className="lcd-audit-section">
                  <h4>DBA (Damage Based Agreement)</h4>
                  {renderYesNoField(
                    "DBA signed and on file?",
                    "dba_signed_and_on_file",
                    auditFormData.dba_signed_and_on_file
                  )}
                  {renderYesNoField(
                    "Does DBA printed name/signature match file?",
                    "dba_printed_name_signature_match_file",
                    auditFormData.dba_printed_name_signature_match_file
                  )}
                  {renderYesNoField(
                    "Does DBA address match file?",
                    "dba_address_match_file",
                    auditFormData.dba_address_match_file
                  )}
                  {renderYesNoField(
                    "Terms of business attached to DBA?",
                    "dba_terms_of_business_agree_to_dba",
                    auditFormData.dba_terms_of_business_agree_to_dba
                  )}
                </div>

                {/* FOA Section */}
                <div className="lcd-audit-section">
                  <h4>FOA (Form of Authority)</h4>
                  {renderYesNoField(
                    "FOA signed and on file?",
                    "foa_signed_and_on_file",
                    auditFormData.foa_signed_and_on_file
                  )}
                  {renderYesNoField(
                    "FOA and DBA signature match?",
                    "foa_and_dba_signature_match",
                    auditFormData.foa_and_dba_signature_match
                  )}
                </div>

                {/* KYC Section */}
                <div className="lcd-audit-section">
                  <h4>KYC (Know Your Customer)</h4>
                  {renderYesNoField(
                    "Credit check on file?",
                    "kyc_credit_check_on_file",
                    auditFormData.kyc_credit_check_on_file
                  )}
                  {renderYesNoField(
                    "Name match?",
                    "kyc_name_match",
                    auditFormData.kyc_name_match
                  )}
                  {renderYesNoField(
                    "Address match?",
                    "kyc_address_match",
                    auditFormData.kyc_address_match
                  )}
                  {renderYesNoField(
                    "PEP/Sanctions clear?",
                    "kyc_pep_sanctions_clear",
                    auditFormData.kyc_pep_sanctions_clear
                  )}
                  {renderYesNoField(
                    "Electoral roll confirmed?",
                    "kyc_electoral_roll_confirmed",
                    auditFormData.kyc_electoral_roll_confirmed
                  )}
                  {renderYesNoField(
                    "Active credit accounts found?",
                    "kyc_active_credit_accounts_found",
                    auditFormData.kyc_active_credit_accounts_found
                  )}
                </div>

                {/* Notes Section */}
                <div className="lcd-audit-section">
                  <h4>Additional Notes</h4>
                  <textarea
                    className="lcd-notes-textarea"
                    placeholder="Enter any additional notes or comments..."
                    value={auditFormData.notes}
                    onChange={(e) => handleAuditInputChange('notes', e.target.value)}
                    rows={4}
                  />
                </div>

                {/* Message Display */}
                {auditMessage && (
                  <div className={`lcd-message ${auditMessage.includes('success') ? 'success' : 'error'}`}>
                    {auditMessage}
                  </div>
                )}

                {/* Form Actions */}
                <div className="lcd-audit-form-actions">
                  <button
                    type="submit"
                    disabled={isSubmittingAudit}
                    className="lcd-submit-button"
                  >
                    {isSubmittingAudit ? 'Creating Audit...' : 'Create Audit'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAuditForm(false)}
                    disabled={isSubmittingAudit}
                    className="lcd-cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Email Composer Modal */}
        {showEmailComposer && (
          <EmailComposer
            legacyClaimId={selectedClaimForCorrespondence || undefined}
            defaultTo={claimant.email || ''}
            defaultSubject={`Regarding your claim - ${claimant.firstName} ${claimant.lastName}`}
            defaultBody={`Dear ${claimant.firstName} ${claimant.lastName},\n\n`}
            onClose={() => setShowEmailComposer(false)}
            onSent={() => {
              setShowEmailComposer(false);
              // Refresh correspondence if viewing a specific claim
              if (selectedClaimForCorrespondence) {
                // The LegacyClaimCorrespondence component will refresh automatically
              }
            }}
          />
        )}

        {/* Upload Document Dialog */}
        {showUploadDialog && (
          <div className="lcd-upload-dialog-overlay" onClick={() => setShowUploadDialog(false)}>
            <div className="lcd-upload-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="lcd-upload-dialog-header">
                <h3>Upload Document</h3>
                <button 
                  className="lcd-close-button" 
                  onClick={() => {
                    setShowUploadDialog(false);
                    setSelectedFile(null);
                    setDocumentType('correspondence');
                    setDocumentDescription('');
                    setUploadError(null);
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="lcd-upload-dialog-content">
                <div className="lcd-upload-form">
                  <div className="lcd-form-group">
                    <label htmlFor="document-type">Document Type</label>
                    <select
                      id="document-type"
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      className="lcd-form-select"
                    >
                      <option value="correspondence">General Correspondence</option>
                      <option value="letter">Letter</option>
                      <option value="email">Email</option>
                      <option value="fax">Fax</option>
                      <option value="memo">Memo</option>
                      <option value="notice">Notice</option>
                      <option value="agreement">Agreement</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="lcd-form-group">
                    <label htmlFor="file-input">Select File</label>
                    <input
                      ref={fileInputRef}
                      id="file-input"
                      type="file"
                      onChange={handleFileSelect}
                      className="lcd-file-input"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.eml,.msg"
                    />
                    {selectedFile && (
                      <div className="lcd-selected-file">
                        Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </div>
                    )}
                  </div>

                  <div className="lcd-form-group">
                    <label htmlFor="description">Description (Optional)</label>
                    <textarea
                      id="description"
                      value={documentDescription}
                      onChange={(e) => setDocumentDescription(e.target.value)}
                      className="lcd-form-textarea"
                      rows={3}
                      placeholder="Add any notes or description about this document..."
                    />
                  </div>

                  {uploadError && (
                    <div className="lcd-upload-error">
                      {uploadError}
                    </div>
                  )}
                </div>
              </div>
              <div className="lcd-upload-dialog-actions">
                <button 
                  className="lcd-cancel-button" 
                  onClick={() => {
                    setShowUploadDialog(false);
                    setSelectedFile(null);
                    setDocumentType('correspondence');
                    setDocumentDescription('');
                    setUploadError(null);
                  }}
                  disabled={uploadingDocument}
                >
                  Cancel
                </button>
                <button 
                  className="lcd-upload-button"
                  onClick={handleDocumentUpload}
                  disabled={!selectedFile || uploadingDocument}
                >
                  {uploadingDocument ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacyClaimantDetails;